/** One generated cave: draw, finish, check; retry with the next sub-seed; fall back (docs/spec/03 §4). */

import { mixSeed, Rng } from '../../sim/rng';
import type { CaveDef } from '../types';
import { pickBonus } from './bonus';
import { placeKeys } from './deep';
import { fallbackCave } from './fallback';
import { finishCave, whyNot } from './finish';
import { baseFill, clearDoorstep, clearPocket, placeExit, placeInbox } from './layout';
import { caveColors, caveName } from './names';
import type { Plan } from './plan';
import { quotaShare } from './plan';
import { Sketch } from './sketch';
import { headline, pickFeatures } from './table';

/** Draws before the fallback: the tenth failure returns it. */
export const MAX_ATTEMPTS = 10;

export interface GenerateRequest {
  readonly seed: number;
  readonly depth: number;
  /** Allow the Deep elements. */
  readonly deep: boolean;
  /** A 20 x 12 bonus cave (an intermission) instead of 40 x 22. */
  readonly bonus: boolean;
  readonly id: string;
}

export interface GenerateReport {
  readonly def: CaveDef;
  /** Draws made, 1 for a first-time success. */
  readonly attempts: number;
  readonly fallback: boolean;
  /** Why each failed draw was turned down. */
  readonly rejections: readonly string[];
}

/** Depth as a whole number from 1; anything odd counts as 1. */
export function clampDepth(depth: number): number {
  return Number.isFinite(depth) ? Math.max(1, Math.floor(depth)) : 1;
}

/** One draw from one sub-seed: a finished cave, or why it could not be finished. */
function draw(seed: number, req: GenerateRequest, depth: number): CaveDef | string {
  const rng = new Rng(seed);
  const bonus = req.bonus ? pickBonus(rng, depth, req.deep) : undefined;
  const sk = bonus ? new Sketch(20, 12) : new Sketch(40, 22);
  baseFill(sk, rng, depth, bonus ? 4 + bonus.gems : 0);
  const inbox = placeInbox(sk, rng, req.bonus);
  const plan: Plan = {
    depth,
    bonus: req.bonus,
    inbox,
    exit: undefined,
    creatures: [],
    keys: [],
    placed: [],
    amoebaTime: 0,
    magicWallTime: 0,
  };
  // A full cave fixes its exit first; a bonus cave fits the exit round its puzzle.
  if (!bonus) plan.exit = placeExit(sk, rng, inbox, false);
  for (const f of bonus ? bonus.features : pickFeatures(rng, depth, req.deep)) {
    if (f.build(sk, rng, plan)) plan.placed.push(f.id);
  }
  if (bonus && plan.placed.length === 0) return 'no room for the bonus puzzle';
  if (bonus) plan.exit = placeExit(sk, rng, inbox, true);
  const exit = plan.exit;
  if (!exit) return 'no room for the exit';
  if (!placeKeys(sk, rng, plan)) return 'no home for a key';
  clearPocket(sk, inbox);
  clearDoorstep(sk, exit);
  const theme = headline(rng, plan.placed);
  const def = finishCave(
    {
      id: req.id,
      name: caveName(rng, theme),
      theme,
      colors: caveColors(rng, req.deep),
      map: sk.rows(),
      bonus: req.bonus,
      amoebaTime: plan.amoebaTime,
      magicWallTime: plan.magicWallTime,
      // Bonus caves ask a little more: failing one costs no life.
      share: quotaShare(depth) + (rng.int(7) - 3) / 100 + (bonus ? 0.1 + bonus.greed : 0),
    },
    depth,
    seed,
  );
  return def ?? 'too few diamonds in reach, or no route to the exit';
}

/** The cave for a request, with how it went: attempts, fallback, and why draws were turned down. */
export function generateReport(req: GenerateRequest): GenerateReport {
  const depth = clampDepth(req.depth);
  const rejections: string[] = [];
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const sub = mixSeed(req.seed, depth, req.deep ? 1 : 0, req.bonus ? 1 : 0, attempt);
    const drawn = draw(sub, req, depth);
    const why = typeof drawn === 'string' ? drawn : whyNot(drawn);
    if (why !== undefined) {
      rejections.push(why);
      continue;
    }
    if (typeof drawn !== 'string') return { def: drawn, attempts: attempt + 1, fallback: false, rejections };
  }
  const def = fallbackCave(req.id, depth, req.bonus, mixSeed(req.seed, depth));
  return { def, attempts: MAX_ATTEMPTS, fallback: true, rejections };
}
